import React, { useState, useEffect } from "react";
import { CircleFill, Download, EyeFill, HouseDoorFill, Eye } from 'react-bootstrap-icons';
import { toast } from "react-toastify";
import { Form } from "react-bootstrap";
import { reportServiceApi } from "../services/api";
import { prioritiesType, schoolType, setCenterType } from "../types/customTypes";
import { exportSchools } from "../services/pdfService";
import { averageGeolocation, calculateZoomLevel } from "../services/mapHelper"; // Import the utility functions
import "../assets/styles/MapFilter.scss";

const PDF_URL = process.env.REACT_APP_REPESENTATATIVE_CONDITION_PDF_URL;
const priorities = { Low: 'yellow', Medium: 'orange', High: 'red', PoleAction: '#00a8f3' };
const categoryOrder = ['high schools', 'junior high schools', 'support facilities', 'elementary schools'];
const categoryOrderForfilter = ['high schools', 'junior high schools', 'elementary schools', 'support facilities'];

const MapFilter: React.FC<propTypes> = (props: propTypes) => {
    const [schools, setSchools] = useState({});
    const [filteredSchools, setFilteredSchool] = useState({});
    const [formattedCategories, setFormattedCategories] = useState([]);
    const [phaseObjects, setPhaseObjects] = useState([]);
    const [selectedSchools, setSelectedSchools] = useState({});
    const [isSelectedAll, setIsSelectedAll] = useState(false);
    const [showSpinner, setShowSpinner] = useState(false);
    const [selectedOption, setSelectedOption] = useState(1);

    useEffect(() => {
        getSchoolList();
        sessionStorage.setItem("priority", JSON.stringify(getNumbersFromPriorities()));
    }, [props.schools]);

    const selectAllHandler = async (e) => {
        setIsSelectedAll(e.target.checked);
        // mark all schoolds isChecked true
        const newState = {};
        Object.keys(filteredSchools).forEach(schoolName => {
            newState[schoolName] = e.target.checked;
        });
        setSelectedSchools(newState);

    };

    //commented this for letter use
    const getReportData = () => {
        props.refreshMap();
        const selectedSchoolNames = Object.keys(selectedSchools).filter(schoolName => selectedSchools[schoolName]);
        const selectedSchoolIds = selectedSchoolNames.map(schoolName => schools[schoolName][0].school_Id);
        setShowSpinner(true);
        props.controlImageCreation(true);

        Promise.all([
            createImageUrl(),
            reportServiceApi({
                schoolId: selectedSchoolIds,
                priorityFilterList: getNumbersFromPriorities()
            })
        ])
        .then(([imageUrls, response]) => {
            setShowSpinner(false);
            props.controlImageCreation(false);
            createPDF(response.schools, imageUrls);
        })
        .catch(error => {
            toast.error(error.toString());
        });
    };

    const createImageUrl = async () => {
        const imageUrls = [];

        for (const schoolName in selectedSchools) {
            if (selectedSchools[schoolName]) {
                goToSchool(schoolName);
                const dataUrl = await props.getMapImage();

                imageUrls.push({ schoolName, dataUrl, schoolId: schools[schoolName][0].school_Id });
            }
        }
        return imageUrls;
    };

    const createPDF = (pdfSchools, imageUrls) => {
        const schoolsData = pdfSchools.map(school => {
            school.image = imageUrls.find((img) => img.schoolName === school.schoolName);
            return school;
        });
        exportSchools(schoolsData);
    };

    const selectSchoolHandler = (e) => {
        // set new schools array with isChecked modified as per checkbox
        setSelectedSchools(prevState => {
            const newState = { ...prevState };
            newState[e.target.attributes.id.value] = e.target.checked;
            // make selectAll true if no unChecked school found vice versa
            setIsSelectedAll(Object.values(newState).filter(value => value === true).length === Object.keys(filteredSchools).length);
            return newState;
        });

    };

    const getSchoolList = () => {
        setSchools(props.schools);
        // Flatten the arrays within the object
        const flattenedResponse = Object.values(props.schools).flat();
        generateFormattedCategories(flattenedResponse);
        setFilteredSchool(props.schools);
    };

    const generateFormattedCategories = (flattenedResponse) => {
        const uniqueCategories: Set<string> = new Set(flattenedResponse.map(item => item['school_Category']));
        const orderedCategories = Array.from(uniqueCategories).sort((a, b) => {
            const indexA = categoryOrderForfilter.indexOf(a.toLowerCase());
            const indexB = categoryOrderForfilter.indexOf(b.toLowerCase());
          
            // Compare based on category order
            if (indexA < indexB) return -1;
            if (indexA > indexB) return 1;
          
            return 0; // Categories are the same, no need to change order
        });

        const formattedCategories = Array.from(orderedCategories).map((category, index) => ({
            id: index + 2,  // Assuming you want to start IDs from 2
            key: 'school_Category',
            value: category,
            label: category,
        }));
        setFormattedCategories(formattedCategories);
        
        const uniquePhases: Set<string> = new Set(flattenedResponse.map(item => item['phase']));
        const lastCategoryId = formattedCategories.length > 0 ? formattedCategories[formattedCategories.length - 1].id : 1;
        const phasesArray = Array.from({ length: uniquePhases.size }, (_, index) => ({
            id: lastCategoryId + index + 1,
            key: 'phase',
            value: index + 1,
            label: `Phase-${index + 1}`,
        }));

        setPhaseObjects(phasesArray);
    };

    const disablePriorityFilter = (priority) => {
        return props.priorities[priority] && Object.values(props.priorities).filter(value => value).length === 1;
    };

    const renderPriority = (priority, color) => {
        return (
            <div key={priority} className="form-check form-switch col-12 form-check-inline p-0">
                <CircleFill className="col-1 border border-dark" style={{ borderRadius: '20px', width: 'auto' }} color={color} size={22} />
                <label className="form-check-label ps-2">{priority === "PoleAction"? 'Action Item': priority }</label>
                <input
                    data-testid={`${priority}-radio`}
                    className="form-check-input float-end pointer"
                    checked={props.priorities[priority]}
                    type="checkbox" role="switch"
                    onChange={(e) => props.setPriorities({ ...props.priorities, [priority]: e.target.checked })}
                    disabled={disablePriorityFilter(priority)} />
            </div>
        );
    };

    const getNumbersFromPriorities = () => { // convert priorities to numbers for api requirement.
        const priorityFilterList = [];
        Object.keys(priorities).forEach((key, index) => {
            if (props.priorities[key])
                priorityFilterList.push(index + 1);
        });
        return priorityFilterList;
    };

    const displaySchoolName = str => str.substring(0, 29) + (str.length > 29 ? '...' : '');

    const goToSchool = (schoolName) => {
        setTimeout(() => {
            props.setCenter({
                ...filteredSchools[schoolName][0],
                ...averageGeolocation(filteredSchools[schoolName])
            }, calculateZoomLevel(props.map,filteredSchools[schoolName]));
            props.collapseSidebar();
        });
    };

    const compareSort = (school1, school2) => {
        const category1 = filteredSchools[school1][0].school_Category.toLowerCase();
        const category2 = filteredSchools[school2][0].school_Category.toLowerCase();
      
        const index1 = categoryOrder.indexOf(category1);
        const index2 = categoryOrder.indexOf(category2);
      
        // Compare based on category order
        if (index1 < index2) return -1;
        if (index1 > index2) return 1;
      
        // If categories are the same, compare by school name
        if (category1 === category2) {
          return school1.localeCompare(school2);
        }
      
        return 0; // Categories are the same, no need to change order
    };

    const sortAndFilterByPhase = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const selectedValue = parseInt(e.target.value);
        const fOption = filterOptions.find(o => o.id === selectedValue)

        setSelectedSchools({});
        setSelectedOption(selectedValue);
        setIsSelectedAll(false);
        
        if (fOption) { // Check if fOption is defined
            const filteredData = Object.keys(schools).reduce((acc, key) => {
                if (fOption.key === 'all' || (schools[key][0] && schools[key][0][fOption.key] === fOption.value)) {
                    acc[key] = schools[key];
                }
                return acc;
            }, {});
    
            setFilteredSchool(filteredData);
            props.updateFilteredSchools(filteredData);
        }
    };


    const filterOptions = [
        {id:1, key: 'all', value: 'All', label: 'All'},
        ...formattedCategories,
        ...phaseObjects
    ];

    return (
        <>
            {Object.keys(filteredSchools).length > 0 &&
                <div className="filer" id="Filter-component" data-testid="MapFilter">
                    <div id="priority-filter" className="sider-bar-border mb-1 p-2 mt-2 pb-0 position-relative text" data-testid="priorityFilter">
                        {Object.keys(priorities).map(key => {
                            return renderPriority(key, priorities[key])
                        })}
                        <div className="form-check form-switch col-12 form-check-inline p-0">
                            <a href={PDF_URL} target="_blank" rel="noreferrer" className="float-end pointer">Representative Conditions</a>
                        </div>
                    </div>

                    <div className="sider-bar-border filter-box2 text mt-2 position-relative" data-testid="schoolFilter">
                        <div className="select-all-div border-bottom p-1">
                            <input
                                type="checkbox"
                                className="p-2 mt-2"
                                data-testid="selectAllCheckbox"
                                checked={isSelectedAll}
                                onChange={selectAllHandler}
                            />
                            <label className="ms-1 mt-2">Select All</label>
                            <a
                                onClick={() => {
                                    props.refreshMap();
                                    props.collapseSidebar();
                                    setFilteredSchool(schools);
                                    setSelectedOption(1);
                                    setIsSelectedAll(false);
                                    setSelectedSchools({})
                                }} className="float-end mt-2 me-1 pointer text-reset" title="Refresh Map">
                                    <HouseDoorFill size="20" data-testid="refreshMapButton" />
                            </a>
                            <button
                                disabled={Object.values(selectedSchools).filter(v => v).length === 0}
                                type="button"
                                data-testid="downloadButton"
                                onClick={getReportData}
                                className="download-pdf-btn btn btn-light float-end p-1 text1 mt-1">
                                Download Report <Download className="float-end mt-1 ms-1" />
                            </button>
                        </div>
                        <div className="row p-1">
                            <div className="col-4 pe-0 ps-4">
                                <label className="col-form-label">Filter by:</label>
                            </div>
                            <div className="col-8 ps-0">
                                <Form.Select aria-label="Default select example" data-testid="select-category" size="sm" value={selectedOption} onChange={sortAndFilterByPhase}>
                                    {filterOptions.map(opt => {
                                        return (<option key={opt.id} value={opt.id}>{opt.label}</option>)
                                    })}
                                </Form.Select>
                            </div>
                        </div>
                        <div className="schools-div p-1 text">
                            {Object.keys(filteredSchools).sort(compareSort).map(schoolName => {
                                return (
                                    <div key={schoolName}>
                                        <input
                                            id={schoolName}
                                            data-testid={`${schoolName}-checkbox`}
                                            type="checkbox"
                                            checked={selectedSchools[schoolName] || false}
                                            onChange={(e) => selectSchoolHandler(e)}
                                        />
                                        <label className="ms-1 d-inline" title={schoolName}> {displaySchoolName(schoolName)}</label>
                                        {props.selectedSchool === schoolName ?
                                            <EyeFill
                                                data-testid={`${schoolName}-eye`}
                                                onClick={() => goToSchool(schoolName)}
                                                color="red"
                                                className="float-end me-1 pointer"
                                            />
                                            : <Eye
                                                data-testid={`${schoolName}-eye`}
                                                onClick={() => goToSchool(schoolName)}
                                                className="float-end me-1 pointer"
                                            />
                                        }
                                        <br />
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            }
            {showSpinner &&
                (<div className="d-flex justify-content-center cover-spinner">
                    <div className="spinner-border position-fixed top-50" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>)
            }
        </>
    );
};

type propTypes = {
    setCenter: setCenterType,
    setPriorities: (a) => void,
    priorities: prioritiesType;
    refreshMap: () => void,
    schools: schoolType,
    selectedSchool: string,
	updateFilteredSchools: (schools: schoolType) => void
    collapseSidebar: () => void;
    getMapImage: () => void;
    controlImageCreation : (a) => void;
    map: unknown;
};

export default MapFilter;
